import { Component, Input, OnInit, ViewChild, HostListener, ViewChildren, QueryList, ElementRef, Output, EventEmitter, SecurityContext } from '@angular/core';
import { debounceTime, Subject, Subscription, take } from 'rxjs';
import { IFilter } from '../_models/IFilter';
import { IHeader } from '../_models/IHeader';
import { GridService } from 'app/_services/grid.service';
import { FilterController } from 'app/_controllers/filterController';
import { AppComponent } from 'app/app.component';
import { GridController } from 'app/_controllers/gridController';
import { SelectExpandController } from 'app/_controllers/selectExpandController';
import { ISelectedLine } from 'app/_models/ISelectedLine';
import { ILine } from 'app/_models/ILine';
import { IGridData } from 'app/_models/IGridData';
import { IGridConfig } from 'app/_models/IGridConfig';
import { CommunicationService } from 'app/_services/communication.service';
import { ISettings } from 'app/_models/ISettings';
import {
  IconDefinition, faThumbTack, faFilter, faTableColumns, faMaximize, faArrowUpLong, faAngleDoubleLeft,
  faArrowDownLong, faDownLeftAndUpRightToCenter, faCheck, faChevronRight, faChevronDown,
  faAngleDoubleUp, faInfoCircle, faArrowUpRightFromSquare, faPlus, faXmark, faFileExport, faCopy, faSquareXmark, faTableCells,
  faPaste, faEraser, faCircleDot, faRemove, faEyeSlash, faUpDown, faAngleDoubleDown, faAngleDown, faAngleUp, faSquareCheck, faNetworkWired,
  faChartSimple, faCaretDown, faCaretUp, faAnglesLeft, faChevronLeft, faAnglesRight, faCircle, faArrowUpWideShort, faSquare, faBorderAll
} from '@fortawesome/free-solid-svg-icons';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgZone } from '@angular/core';

@Component({
  selector: 'app-detail-grid',
  templateUrl: './detail-grid.component.html',
  styleUrls: ['./detail-grid.component.css']
})
export class DetailGridComponent implements OnInit {
  //#region Input Fields
  @Input() settings: ISettings;
  @Input() children: IGridConfig[] = [];
  @Input() rowId: any;
  @Input() nodeId: string;
  @Input() userId: string;
  @Input() username: string;  
  @Input() parentGridSelectedLines: any;
  @Input() parentGridExpandedLine: any;
  @Input() subDetail: boolean = false;
  @Input() mobileView: boolean;
  @Input() mainGridName: string;
  //#endregion

  //#region Global Collections  
  headers: IHeader[] = [];
  lines: ILine[] = [];
  selectedLines: ISelectedLine[] = [];
  columnEditorHeaders: IHeader[] = [];
  expandedRows: number[] = [];
  serverDateFormat: string;
  reloadingGridFromError: boolean = false;
  //#endregion

  //#region Grid Layout Fields  
  compactGrid: boolean;
  colWidths: string[] = [];
  prevColWidths: string[] = [];
  reorderedHeaders: IHeader[] = [];
  saveUpdatedLayout: boolean = false;
  //#endregion

  //#region Sort & Filter Fields
  sortOrder: boolean = true;
  colSortedBy: string;
  sortString: string;
  showFilters: boolean = false;
  filterString: string;
  filters: IFilter[] = [];
  showNoRecordsFoundLabel: boolean = false;
  showAdvancedFiltersButton: boolean = false;
  editableFilterString: string = '';
  editableFilterStringError: string = '';
  filteredByEditableFilterString: boolean = false;
  showAdvancedFilters: boolean = false;
  filteredFromHistory: boolean = false;
  initialViewLoad: boolean = true;
  filteredByBlur: boolean = false;
  showMobileFilters: boolean = false;
  showMobileSort: boolean = false;
  activeFilterCount: number = 0;
  //#endregion

  //#region Pagination Fields  
  pageSize: number = 50;
  totalRecords: number = 0;
  pageCount: number = 1;
  offset: number = 0;
  currentpage: number = 1;
  rowCountFrom: number;
  rowCountTo: number;
  //#endregion

  //#region Misc Fields   
  currentViewName: string;
  previousViewName: string = '';
  hasChildren: boolean = false;
  selectedTabIndex: number = 0;
  shiftSelect: boolean = false;
  controlSelect: boolean = false;
  showImageView: boolean = false;
  imageToExpand: any;
  filterUpdated: Subject<string> = new Subject<string>();
  @ViewChild(DetailGridComponent) detailGrid: DetailGridComponent;
  sortChanged: Subject<string> = new Subject<string>();
  showDetailLoader: boolean = false;
  zoomedDetailView: boolean = false;
  contextMenuVisible: boolean = false;
  contextMenuTop: number = 0;
  contextMenuLeft: number = 0;
  contextMenuType: string;
  contextMenuObject: IFilter | IHeader | ILine;
  selectedText: string;
  @Output() notify = new EventEmitter<string>();
  dataSub?: Subscription;
  i: number = 0;
  @ViewChild('root', { static: true }) root!: ElementRef;
  @Output() tableReady = new EventEmitter<HTMLElement>();
  x = false;
  mobileTileSize: string = 'Medium';
  longPressTimer: any;
  applyZIndexToMobileDetailGrids: boolean = false;
  //#endregion   

  //#region Icons
  faThumbTack: IconDefinition = faThumbTack;
  faFilter: IconDefinition = faFilter;
  faTableColumns: IconDefinition = faTableColumns;
  faMaximize: IconDefinition = faMaximize;
  faArrowUpLong: IconDefinition = faArrowUpLong;
  faArrowDownLong: IconDefinition = faArrowDownLong;
  faDownLeftAndUpRightToCenter: IconDefinition = faDownLeftAndUpRightToCenter;
  faCheck: IconDefinition = faCheck;
  faChevronRight: IconDefinition = faChevronRight;
  faChevronDown: IconDefinition = faChevronDown;
  faAngleDoubleUp: IconDefinition = faAngleDoubleUp;
  faInfoCircle: IconDefinition = faInfoCircle;
  faArrowUpRightFromSquare: IconDefinition = faArrowUpRightFromSquare;
  faPlus: IconDefinition = faPlus;
  faXmark: IconDefinition = faXmark;
  faFileExport: IconDefinition = faFileExport;
  faCopy: IconDefinition = faCopy;
  faPaste: IconDefinition = faPaste;
  faEraser: IconDefinition = faEraser;
  faCircleDot: IconDefinition = faCircleDot;
  faRemove: IconDefinition = faRemove;
  faEyeSlash: IconDefinition = faEyeSlash;
  faUpDown: IconDefinition = faUpDown;
  faAngleDown: IconDefinition = faAngleDown;
  faAngleDoubleDown: IconDefinition = faAngleDoubleDown;
  faAngleUp: IconDefinition = faAngleUp;
  faSquareCheck: IconDefinition = faSquareCheck;
  faSquareXmark: IconDefinition = faSquareXmark;
  faChartSimple: IconDefinition = faChartSimple;
  faCaretDown: IconDefinition = faCaretDown;
  faCaretUp: IconDefinition = faCaretUp;
  faAnglesLeft: IconDefinition = faAnglesLeft;
  faChevronLeft: IconDefinition = faChevronLeft;
  faAnglesRight: IconDefinition = faAnglesRight;
  faCircle: IconDefinition = faCircle;
  faArrowUpWideShort: IconDefinition = faArrowUpWideShort;
  faSquare: IconDefinition = faSquare;
  faBorderAll: IconDefinition = faBorderAll;
  faTableCells: IconDefinition = faTableCells;
  faNetworkWired: IconDefinition = faNetworkWired;
  faAngleDoubleLeft: IconDefinition = faAngleDoubleLeft;
  //#endregion

  constructor(
    public app: AppComponent,
    private filterController: FilterController,
    private gridController: GridController,
    private selectExpandController: SelectExpandController,
    private gridService: GridService,
    private communicationService: CommunicationService,
    private sanitizer: DomSanitizer
  ) {
    this.communicationService.rad_detailgrid_adjustZIndex$.subscribe((tabOpened) => {      
      this.applyZIndexToMobileDetailGrids = tabOpened;
    });

    this.filterUpdated.pipe(debounceTime(1250)).subscribe(() => {
      if (this.settings.filterDelaySetting) this.buildFilterString();
    });

    this.sortChanged.pipe(debounceTime(1000)).subscribe(() => {
      this.getData();
    });
  }
  
  ngOnInit(): void {
    //console.log(this.children);
    //if (this.mobileView) return;
    this.compactGrid = this.settings.compactGridSetting;
    for (let i = 0; i < this.children.length; i++) {
      this.children[i].pinned = false;
      this.children[i].showPin = false;
    }

    if (localStorage.getItem(`qx_viewPin_${this.nodeId}`)) {
      let childIndex = this.children.findIndex(child => child.name == localStorage.getItem(`qx_viewPin_${this.nodeId}`));
      if (childIndex != -1) {
        this.children[childIndex].pinned = true;
        this.children[childIndex].showPin = true;
        this.selectedTabIndex = childIndex;
      }
    }

    this.sortString = '';
    this.filterString = '';
    this.getData();
  }

  changeTab(event: any): void {
    if (this.mobileView) {
      //console.log(event);
      this.currentViewName = event.srcElement.value.toString();
      let index = this.children.findIndex(c => c.name == this.currentViewName);
      if (index !== -1) this.selectedTabIndex = index;
      //return;
    } else {
      this.currentViewName = this.children[event.index].name;
      this.selectedTabIndex = event.index;
    }

    this.showFilters = false;
    this.sortString = '';
    this.filterString = '';
    this.editableFilterString = '';
    this.filteredByEditableFilterString = false;
    this.showAdvancedFilters = false;
    this.offset = 0;
    this.currentpage = 1;
    this.pageSize = 50;
    this.headers = [];
    this.lines = [];
    this.filters = [];
    this.selectedLines = [];        
    this.deselectAll();
    this.getData();
  }

  getData(): void {
    //#region Setup 
    this.dataSub?.unsubscribe();
    if (this.filteredFromHistory) this.showDetailLoader = true;
    if (!this.settings.detailFiltersByRowSetting && localStorage.getItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}`) && !this.reloadingGridFromError)
      this.filterString = JSON.parse(localStorage.getItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}`)).serverString;

    if (this.settings.detailFiltersByRowSetting && localStorage.getItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`) && !this.reloadingGridFromError)
      this.filterString = JSON.parse(localStorage.getItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`)).serverString;

    if (!this.settings.detailFiltersByRowSetting && localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`) && !this.reloadingGridFromError)
      this.filterString = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`)).filterString;

    if (this.settings.detailFiltersByRowSetting && localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`) && !this.reloadingGridFromError)
      this.filterString = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`)).filterString;

    if (localStorage.getItem(`qx_sort_${this.nodeId}_${this.children[this.selectedTabIndex].name}`) && !this.reloadingGridFromError)
      this.sortString = JSON.parse(localStorage.getItem(`qx_sort_${this.nodeId}_${this.children[this.selectedTabIndex].name}`)).sortString;

    this.showNoRecordsFoundLabel = false;
    let keys = ['nodeId', 'viewName', 'sortString', 'filterString', 'pageSize', 'keyValue', 'offset', 'username', 'userId', 'advancedFilterString', 'reloadingGridFromError', 'freeColumnResizingEnabled'];
    let values = [this.nodeId, this.children[this.selectedTabIndex].name, this.sortString, this.filterString, this.pageSize.toString(), this.rowId.toString(), this.offset.toString(),
    this.username, this.userId, this.filteredByEditableFilterString.toString(), this.reloadingGridFromError.toString(), this.settings.freeColumnResizeSetting.toString()];
    let formData = this.app.buildForm(keys, values);
    //#endregion        
    this.dataSub = this.gridService.getData(formData).subscribe({
      next: (data: IGridData) => {
        this.reloadingGridFromError = false;
        this.serverDateFormat = data.dateFormat;
        this.headers = data.headers;

        if (!this.settings.detailFiltersByRowSetting && localStorage.getItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}`)) {
          this.editableFilterString = JSON.parse(localStorage.getItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}`)).userString;
          this.showAdvancedFilters = true;
          this.showFilters = false;
          this.filters = this.filterController.clearAllFilters(this.filters);
        }

        if (this.settings.detailFiltersByRowSetting && localStorage.getItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`)) {
          this.editableFilterString = JSON.parse(localStorage.getItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`)).userString;
          this.showAdvancedFilters = true;
          this.showFilters = false;
          this.filters = this.filterController.clearAllFilters(this.filters);
        }

        if (!this.settings.detailFiltersByRowSetting && localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`)) {
          this.filters = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`)).filters;
          if (this.filters.filter(filter => filter.isActive).length > 0) {
            this.showFilters = this.filterString.length > 0 ? true : false;
            if (this.showFilters) this.refocusFilters();
          }
        }

        if (this.settings.detailFiltersByRowSetting && localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`)) {
          this.filters = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`)).filters;
          if (this.filters.filter(filter => filter.isActive).length > 0) {
            this.showFilters = this.filterString.length > 0 ? true : false;
            if (this.showFilters) this.refocusFilters();
          }
        }

        if (this.filters.length == 0)
          this.filters = this.filterController.loadFilters(this.headers);

        let result = this.gridController.formatSanitise(data.lines, this.headers, this.filters, this.settings.dateFormatSetting, this.nodeId, this.children[this.selectedTabIndex].name);
        this.headers = result.headers;
        this.lines = result.lines;
        this.filters = result.filters;
        this.communicationService.updateRadHeaders(this.headers, this.serverDateFormat);

        if (this.mobileView) this.activeFilterCount = this.filters.filter(filter => filter.isActive).length;
        if (data.lines.length == 0) {
          this.lines = [];
          this.showNoRecordsFoundLabel = true;
          this.totalRecords = 0;
          this.rowCountFrom = 0;
          this.rowCountTo = 0;
          this.showDetailLoader = false;
          return;
        }

        if (this.settings.detailFiltersByRowSetting && localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`)) {
          if (this.filters.filter(filter => filter.isActive).length == 0) {
            localStorage.removeItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`);
            this.filterString = '';
          } else {
            localStorage.setItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`, JSON.stringify({ filterString: this.filterString, filters: this.filters }));
          }
        } else if (localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`)) {
          if (this.filters.filter(filter => filter.isActive).length == 0) {
            localStorage.removeItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`);
            this.filterString = '';
          } else {
            localStorage.setItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`, JSON.stringify({ filterString: this.filterString, filters: this.filters }));
          }
        }

        this.hasChildren = data.hasChildren;
        this.totalRecords = data.totalRecords;
        this.calculatePageCount();
        this.reExpandRows();
        this.reSelectRows();
        this.colWidths = [];
        this.reorderedHeaders = [];
        if (this.currentViewName == 'master' || this.currentViewName == '' || this.currentViewName == undefined) this.currentViewName = data.masterViewName;
        if (this.previousViewName.length == 0) this.previousViewName = this.children[this.selectedTabIndex].name;
        this.showDetailLoader = false;
        sessionStorage.setItem('isMaster', 'false');
        this.rowCountFrom = this.currentpage > 1 ? this.offset : 1;
        this.rowCountTo = this.currentpage > 1 ? this.offset + this.lines.length : this.lines.length;
        this.filteredFromHistory = false;
        this.getHeaderCalculationsFromStorage();
        this.notify.emit('');
        if (!this.mobileView && this.root) this.tableReady.emit(this.root.nativeElement);
        this.sortOrder = this.sortString && this.sortString.includes('asc') ? true : false;        
        if (this.parentGridSelectedLines && this.parentGridSelectedLines.length == 0) {
          this.communicationService.filterRadButtons(this.currentViewName, []);
        }
      }, error: (errorLog) => {
        this.showDetailLoader = false;
        this.gridController.handleGridError(errorLog, this.nodeId, this.children[this.selectedTabIndex].name, this.settings.detailFiltersByRowSetting ? this.rowId.toString() : undefined, this.settings);
        this.reloadingGridFromError = true;
        this.getData();
      }
    });
  }

  //#region Grid Layout 
  toggleZoomDetailView(): void {
    this.filters.forEach(f => f.focused = false);
    this.zoomedDetailView = !this.zoomedDetailView;
    this.communicationService.zoomIntoDetailView();
  }

  @ViewChild('tablerowInner') rowHeader!: any;
  handleColResize(): void {
    let children = this.rowHeader.nativeElement.parentNode.children;
    let visibleHeaders = this.headers.filter(header => header.visible);
    for (let index = 0; index < children.length; index++) {
      let resizedHeader = visibleHeaders.find(header => children[index].innerText.trim().includes(header.caption));
      if (resizedHeader) resizedHeader.width = `${children[index].clientWidth}px`;
    }

    this.gridController.saveLayout(this.nodeId, this.children[this.selectedTabIndex].name, this.username, this.headers).then(() => { });
  }

  handleColReorder(): void {
    this.gridController.saveLayout(this.nodeId, this.children[this.selectedTabIndex].name, this.username, this.headers).then(() => { });
  }

  hideColumn(header: IHeader): void {
    let visibleCount = this.headers.filter(header => header.visible).length;
    if (visibleCount == 1) {
      this.app.alertError(this.app.translations.ALERT_Error_AllHeadersHidden);
      return;
    };

    header.visible = false;
    if (this.filters.length > 0) {
      let filterIndex = this.filters.findIndex(filter => filter.field == header.field);
      if (filterIndex != -1) this.filters.splice(filterIndex, 1);
      let filterString = this.filterController.buildFilterString(this.filters);
      localStorage.setItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`, JSON.stringify({ filterString: filterString, filters: this.filters }));
      this.getData();
    }

    this.gridController.saveLayout(this.nodeId, this.children[this.selectedTabIndex].name, this.username, this.headers).then(() => { });
  }
  //#endregion

  //#region Sort & Filter  
  sortFromMobile(): void {
    this.showMobileSort = false;
    let header = this.headers.find(header => header.sortedBy);
    if (header) {
      this.sortString = this.sortOrder ? `${header.field} asc` : `${header.field} desc`;
      localStorage.setItem(`qx_sort_${this.nodeId}_${this.children[this.selectedTabIndex].name}`, JSON.stringify({ header, sortString: this.sortString }));
    } else {
      this.sortString = '';
      this.sortOrder = true;
    }
    this.getData();
  }

  updateSortByColumnMobile(header: IHeader): void {
    if (header.displayFormat == 'IMAGE' || header.dataType == 'Byte[]') return;
    localStorage.removeItem(`qx_sort_${this.nodeId}_${this.children[this.selectedTabIndex].name}`);
    header.sortedBy = !header.sortedBy;
    if (header.sortedBy) this.sortOrder = true;
  }

  sort(header: IHeader): void {
    this.headers.forEach(header => header.sortedBy = false);

    if (!header) {
      localStorage.removeItem(`qx_sort_${this.nodeId}_${this.children[this.selectedTabIndex].name}`);
      this.sortString = '';
      this.sortOrder = true;
      this.getData();
      return;
    }

    if (header.displayFormat == 'IMAGE' || header.dataType == 'Byte[]') return;
    header.sortedBy = !header.sortedBy;
    if (header.sortedBy) this.sortOrder = !this.sortOrder;
    this.sortString = this.sortOrder ? `${header.field} asc` : `${header.field} desc`;
    localStorage.setItem(`qx_sort_${this.nodeId}_${this.children[this.selectedTabIndex].name}`, JSON.stringify({ header, sortString: this.sortString }));
    this.getData();
  }

  updateValue(value: string, filter: IFilter, event: any): void {
    filter.isActive = true;
    filter.isNull = false;
    filter.updated = true;
    filter.value = value == undefined ? event.checked : value;
    if (filter.value.length == 0) {
      filter.isActive = false;
      if (this.filters.filter(f => f.isActive).length == 0) {
        this.pageSize = 20;
        this.filters = this.filterController.clearAllFilters(this.filters);
        this.filterString = '';
        if (this.settings.detailFiltersByRowSetting) localStorage.removeItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`);
        else localStorage.removeItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`);
        this.getData();
        return;
      }
    }

    if (this.mobileView) return;
    if (filter.displayFormat == 'CHECK') {
      this.buildFilterString();
      return;
    }

    if (filter.value && filter.history) {
      filter.filteredHistory = filter.history.filter((entry: string) =>
        entry.toLowerCase().includes(filter.value.toLowerCase())
      );
    } else filter.filteredHistory = [...filter.history];

    if (this.settings.filterDelaySetting && this.filters.filter(f => f.isActive).length > 0) this.filterUpdated.next('');
    else {
      this.unFocusAllFilters();
      this.filteredByBlur = true;
    }
  }

  updateFilterFromHistory(value: string, filter: IFilter): void {
    filter.isActive = true;
    filter.focused = false;
    filter.isNull = false;
    filter.updated = true;
    filter.value = value;
    filter.showHistory = false;

    if (filter.value && filter.history) {
      filter.filteredHistory = filter.history.filter((entry: string) =>
        entry.toLowerCase().includes(value.toLowerCase())
      );
    } else filter.filteredHistory = [...filter.history];

    this.buildFilterString();
  }

  buildFilterString(): void {
    let filter = this.filters.find(filter => filter.updated);
    if (filter) {
      if (filter.history.length >= 0 && !filter.history.find(historyVal => historyVal == filter.value) && filter.value.length > 0) {
        filter.history.unshift(filter.value);
        if (filter.history.length > 5) filter.history.pop();
      }

      this.filterString = this.filterController.buildFilterString(this.filters);
      filter.updated = false;
      this.editableFilterString = '';
      this.filteredByEditableFilterString = false;
      this.showAdvancedFilters = false;
      this.showMobileFilters = false;
      localStorage.removeItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}`);
      if (this.settings.detailFiltersByRowSetting) {
        localStorage.removeItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`);
        localStorage.setItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`, JSON.stringify({ filterString: this.filterString, filters: this.filters }));
      } else {
        localStorage.removeItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`);
        localStorage.setItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`, JSON.stringify({ filterString: this.filterString, filters: this.filters }));
      }

      this.offset = 0;
      this.currentpage = 1;
      this.getData();
    }
  }

  nullifyFilter(filter: IFilter, event: MouseEvent): void {
    if (filter.displayFormat.includes('DATE') || filter.displayFormat == 'TIME') {
      event.preventDefault();
      filter = this.filterController.clearFilter(filter);
    }

    filter.isNull = true;
    filter.isActive = true;
    filter.updated = true;
    this.buildFilterString();
  }

  clearFilter(filter: IFilter): void {
    filter = this.filterController.clearFilter(filter);
    if (this.filters.filter(f => f.isActive).length == 0) {
      this.pageSize = 50;
      this.filters = this.filterController.clearAllFilters(this.filters);
      this.filterString = '';
      if (this.settings.detailFiltersByRowSetting) localStorage.removeItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`);
      else localStorage.removeItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`);
      this.getData();
      return;
    }

    this.buildFilterString();
  }

  copyValue(filter: IFilter): void {
    this.app.clipboard.copy(filter ? filter.value : this.selectedText);
    this.app.alertInfo(this.app.translations.ALERT_Info_Copied);
  }

  pasteValueIntoFilter(filter: IFilter): void {
    navigator.clipboard.readText().then(text => {
      filter.value = text;
      filter.isActive = true;
      filter.isNull = false;
      filter.updated = true;
      filter.filteredHistory = filter.history.filter((entry: string) =>
        entry.toLowerCase().includes(text.toLowerCase())
      );
      this.buildFilterString();
    }).catch();
  }

  fromDateUpdated(selectedDate: string, filter: IFilter): void {
    filter.ranges.serverDateFrom = selectedDate;
    filter.ranges.userDateFrom = this.filterController.getUserDateFormat(selectedDate, this.settings.dateFormatSetting);
  }

  toDateUpdated(selectedDate: string, filter: IFilter): void {
    filter.ranges.serverDateTo = selectedDate;
    filter.ranges.userDateTo = this.filterController.getUserDateFormat(selectedDate, this.settings.dateFormatSetting);
  }

  createRangeFilter(filter: IFilter): void {
    if (filter.displayFormat == 'TIME' && filter.ranges.from.length == 0 && filter.ranges.to.length == 0) {
      this.app.alertError(this.app.translations.ALERT_Error_InvalidDateRange);
      return;
    }

    if (filter.displayFormat != 'TIME' && (filter.ranges.serverDateFrom == '' || filter.ranges.serverDateTo == '' || filter.ranges.serverDateFrom == null || filter.ranges.serverDateTo == null)) {
      this.app.alertError(this.app.translations.ALERT_Error_InvalidDateRange);
      return;
    }

    filter.isNull = false;
    filter.isActive = true;
    filter.updated = true;
    this.buildFilterString();
  }

  getFilterTooltip(filter: IFilter): string {
    if (filter.displayFormat == 'TIME') return `${filter.ranges.from} - ${filter.ranges.to}`;
    return `${filter.ranges.userDateFrom} - ${filter.ranges.userDateTo}`;
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;

    if (!this.showFilters) {
      this.filters = this.filterController.clearAllFilters(this.filters);
      this.filterString = '';
      if (this.settings.detailFiltersByRowSetting)
        localStorage.setItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`, JSON.stringify({ filterString: this.filterString, filters: this.filters }));
      else
        localStorage.setItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`, JSON.stringify({ filterString: this.filterString, filters: this.filters }));

      this.getData();
    }
  }

  preSelectCheckBoxFilter(filter: IFilter): boolean {
    if (!filter.isActive) return false;
    return this.app.tryParseBoolean(filter.value);
  }

  validateFilterString(filterStringFromInput: string): void {
    this.editableFilterStringError = '';
    let results = this.filterController.formatFilterStringDates(filterStringFromInput, this.filters, this.settings.dateFormatSetting);

    if (results.editableFilterStringError == '') {
      let filterString = this.filterController.validateFilterString(filterStringFromInput, this.headers, false);
      if (filterString.includes('ERROR')) {
        this.editableFilterStringError = filterString;
        return;
      }


      if (this.settings.detailFiltersByRowSetting) {
        localStorage.setItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`, JSON.stringify({ userString: results.userString, serverString: results.serverString }));
        localStorage.removeItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`);
      }
      else {
        localStorage.setItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}`, JSON.stringify({ userString: results.userString, serverString: results.serverString }));
        localStorage.removeItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`);
      }


      this.filteredByEditableFilterString = true;
      this.filterString = results.serverString;
      this.offset = 0;
      this.currentpage = 1;
      this.getData();
    } else this.editableFilterStringError = results.editableFilterStringError;
  }

  openAdvancedFilters(): void {
    this.showAdvancedFilters = true;
    this.editableFilterString = this.filterController.createEditableFilterString(this.filters);
  }

  closeAdvancedFilters(): void {
    this.showAdvancedFilters = false;
    this.editableFilterString = '';
    this.filteredByEditableFilterString = false;
    if (this.settings.detailFiltersByRowSetting)
      localStorage.removeItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`);
    else
      localStorage.removeItem(`qx_filters_advanced_${this.nodeId}_${this.children[this.selectedTabIndex].name}`);
    this.filterString = '';
    this.offset = 0;
    this.currentpage = 1;
    this.getData();
  }

  showAdvancedFiltersDialog(): void {
    this.communicationService.showAdvancedFilterDialog(this.filters, this.headers, this.editableFilterString, this.children[this.selectedTabIndex].name, this.serverDateFormat);
  }

  focusFilter(filter: IFilter): void {
    filter.focused = !filter.focused;
    filter.showHistory = filter.focused;
    filter.filteredHistory = filter.history;
    this.filters.forEach(f => { if (f != filter) f.focused = false; });
  }

  unFocusAllFilters(): void {
    let filters = [];
    let filterString = '';
    if (this.settings.detailFiltersByRowSetting && localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`)) {
      filters = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`)).filters;
      filterString = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`)).filterString;
    }

    if (!this.settings.detailFiltersByRowSetting && localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`)) {
      filters = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`)).filters;
      filterString = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`)).filterString;
    }

    filters.forEach(filter => {
      filter.focused = false
      filter.showHistory = false;
    });

    if (this.settings.detailFiltersByRowSetting)
      localStorage.setItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`, JSON.stringify({ filterString: filterString, filters: filters }));
    else
      localStorage.setItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`, JSON.stringify({ filterString: filterString, filters: filters }));
  }

  @ViewChildren('inputRef') inputRefs!: QueryList<ElementRef<HTMLInputElement>>;
  refocusFilters() {
    if (this.filteredByBlur) {
      this.filteredByBlur = false;
      return;
    }

    this.inputRefs.changes.subscribe((refs: QueryList<ElementRef>) => {
      let focusedItemIndex = this.filters.findIndex(item => item.focused);
      if (focusedItemIndex !== -1) {
        this.filters[focusedItemIndex].showHistory = false;
        let input = refs.get(focusedItemIndex);
        input?.nativeElement.focus();
      }
    });
  }
  //#endregion

  //#region Selection & Expansion  
  @ViewChild('filterInput') filterInput!: ElementRef<HTMLInputElement>;
  @HostListener('window:keyup', ['$event'])
  keyUpEvent(event: KeyboardEvent): void {
    if (event.key == 'Shift') this.shiftSelect = false;
    if (event.key == 'Control') this.controlSelect = false;
    if (event.key == 'Enter') {
      if (this.showFilters) {
        if (this.filters.filter(f => f.isActive).length == 0) {
          this.pageSize = 20;
          this.filters = this.filterController.clearAllFilters(this.filters);
          this.filterString = '';
          if (this.settings.detailFiltersByRowSetting) localStorage.removeItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}_${this.rowId}`);
          else localStorage.removeItem(`qx_filters_${this.nodeId}_${this.children[this.selectedTabIndex].name}`);
          this.getData();
        } else this.buildFilterString();
      } else if (this.showAdvancedFilters) {
        this.filterString = this.filterInput.nativeElement.value;
        this.validateFilterString(this.filterInput.nativeElement.value);
      }
    }
  }

  selectLines(selectedLine: ILine, event: MouseEvent): void {
    this.filters.forEach(f => f.focused = false);
    selectedLine.selected = !selectedLine.selected;
    if (this.detailGrid != undefined) this.detailGrid.deselectAll();

    //deselects parent row when selecting a child row
    if (this.parentGridSelectedLines != undefined && this.parentGridExpandedLine != undefined) {
      this.parentGridSelectedLines = [];
      this.parentGridExpandedLine.selected = false;
    }

    let result = this.selectExpandController.selectLines(selectedLine, event.shiftKey, event.ctrlKey, this.selectedLines, this.lines, this.previousViewName, this.currentViewName, true);
    this.selectedLines = result.selectedLines;
    this.lines = result.lines;
    this.currentViewName = result.currentViewName;
    this.communicationService.filterRadButtons(this.currentViewName, this.selectedLines);
  }

  reExpandRows(): void {
    if (sessionStorage.getItem(`${this.nodeId}|Expanded|${this.children[this.selectedTabIndex].name}`)) {
      this.expandedRows = [];
      let expandedIds = sessionStorage.getItem(`${this.nodeId}|Expanded|${this.children[this.selectedTabIndex].name}`).split(';');
      expandedIds.forEach((id) => { this.expandedRows.push(Number(id)); })
    }

    if (this.expandedRows.length == 0) return;
    this.lines = this.selectExpandController.reExpandRows(this.expandedRows, this.lines);
  }

  reSelectRows(): void {
    if (this.selectedLines.length == 0) return;
    this.lines = this.selectExpandController.reSelectRows(this.selectedLines, this.lines);
  }

  deselectAll(): void {
    this.selectedLines = [];
    this.communicationService.filterRadButtons(this.currentViewName, this.selectedLines);
    this.lines.forEach(line => { line.selected = false; })
  }

  selectAll(): void {
    let result = this.selectExpandController.selectAll(this.lines, this.selectedLines);
    this.selectedLines = result.selectedLines;
    this.lines = result.lines;
    this.communicationService.filterRadButtons(this.currentViewName, this.selectedLines);
  }

  collapse(expandedLine: ILine): void {
    let result = this.selectExpandController.collapse(expandedLine, this.expandedRows, this.nodeId, this.children[this.selectedTabIndex].name, this.lines);
    this.expandedRows = result.expandedRows;
    expandedLine = result.expandedLine;
    this.lines = result.lines;
    this.communicationService.filterRadButtons(this.currentViewName, this.selectedLines);
  }

  expand(expandedLine: ILine): void {
    this.filters.forEach(f => f.focused = false);
    let res = this.selectExpandController.expand(expandedLine, this.lines, this.expandedRows, this.children[this.selectedTabIndex].name, this.nodeId);
    this.lines = res.lines;
    this.expandedRows = res.expandedRows;
    expandedLine = res.expandedLine;
  }
  //#endregion

  //#region Pagination
  changePageSize(event: any): void {
    this.offset = 0;
    this.currentpage = 1;
    this.pageSize = event.srcElement.value.toString() == this.app.translations.GBL_lbl_AllPageLength ? 100000 : event.target.value;
    this.getData();
  }

  calculatePageCount(): void {
    this.pageCount = this.totalRecords / this.pageSize;
    if (this.totalRecords % this.pageSize > 0) this.pageCount = this.pageCount + 1;
    this.pageCount = Math.floor(this.pageCount);
  }

  next(): void {
    this.filters.forEach(f => f.focused = false);
    this.offset = this.offset + Number(this.pageSize);
    this.currentpage++;
    this.getData();
  }

  prev(): void {
    this.filters.forEach(f => f.focused = false);
    this.offset = this.offset - Number(this.pageSize);
    if (this.offset < 0) this.offset = 0
    this.currentpage--;
    this.getData();
  }

  first(): void {
    this.filters.forEach(f => f.focused = false);
    this.offset = 0;
    this.currentpage = 1;
    this.getData();
  }

  last(): void {
    this.filters.forEach(f => f.focused = false);
    this.offset = this.totalRecords - this.pageSize;
    if (this.offset < 0) this.offset = 0
    this.currentpage = this.pageCount;
    this.getData();
  }

  isFirstPage(): boolean { return this.currentpage == 1; }
  isLastPage(): boolean { return this.currentpage == this.pageCount; }
  //#endregion

  //#region Misc  
  expandImage(image: any): void {
    this.filters.forEach(f => f.focused = false);
    this.communicationService.showImageDialog(this.app.getFormattedImage(image));
  }

  exportData(): void {
    this.filters.forEach(f => f.focused = false);
    this.communicationService.showExportDialog(this.headers, this.lines, this.children[this.selectedTabIndex].name, this.rowId.toString(), this.filterString);
  }

  openColumnDialog(): void {
    this.filters.forEach(f => f.focused = false);
    this.communicationService.showColumnDialog(this.headers, this.currentViewName);
  }

  checkIndeterminate(value: any): boolean {
    return typeof value == 'boolean' ? false : true
  }

  getViewName(): void {
    sessionStorage.setItem('isMaster', 'false');
  }

  toggleTabs(tab: IGridConfig): void {
    this.filters.forEach(f => f.focused = false);
    this.children.forEach(child => {
      if (child.name != tab.name) {
        child.pinned = false;
        child.showPin = false;
      }
    });

    tab.pinned = !tab.pinned;
    tab.showPin = tab.pinned ? true : false;

    if (tab.pinned) localStorage.setItem(`qx_viewPin_${this.nodeId}`, tab.name);
    else localStorage.removeItem(`qx_viewPin_${this.nodeId}`);
  }

  showPin(tab: IGridConfig): void {
    tab.showPin = true;
  }

  hidePin(tab: IGridConfig): void {
    if (!tab.pinned) tab.showPin = false;
  }

  @ViewChild('contextMenu') contextMenuRef!: ElementRef;
  openContextMenu(event: any, obj: IFilter | IHeader | ILine): void {
    if (this.contextMenuVisible) {
      this.contextMenuVisible = false;
      return;
    }

    event.preventDefault();
    this.contextMenuTop = -9999;
    this.contextMenuLeft = -9999;
    this.contextMenuVisible = true;
    this.contextMenuObject = obj;

    setTimeout(() => {
      let menu = this.contextMenuRef.nativeElement as HTMLElement;
      let menuWidth = menu.offsetWidth;
      let menuHeight = menu.offsetHeight;
      let viewportWidth = window.innerWidth;
      let viewportHeight = window.innerHeight;

      let left = event.clientX;
      let top = event.clientY;
      if (left + menuWidth > viewportWidth) left = viewportWidth - menuWidth - 10;
      if (top + menuHeight > viewportHeight) top = viewportHeight - menuHeight - 10;
      this.contextMenuLeft = Math.max(0, left);
      this.contextMenuTop = Math.max(0, top);

      switch (obj.kind) {
        case 'IFilter':
          this.contextMenuType = 'filter';
          break;
        case 'IHeader':
          this.contextMenuType = 'header';
          break;
        case 'ILine':
          this.contextMenuType = 'line';
          let temp = this.selectedText;
          this.selectedText = window.getSelection() ? window.getSelection().toString() : temp;
          break;
      }
    }, 0);
  }

  @HostListener('document:click')
  closeContextMenu(): void {
    this.contextMenuVisible = false;
  }

  getHeaderCalculation(header: IHeader, type: string): void {
    let keys = ['userId', 'nodeId', 'viewName', 'filterString', 'keyValue', 'headerField', 'calcType'];
    let values = [this.userId, this.nodeId, this.children[this.selectedTabIndex].name, this.filterString, this.rowId.toString(), header.field, type];
    let formData = this.app.buildForm(keys, values);
    this.gridService.getHeaderCalculation(formData).subscribe({
      next: (data: any) => {
        if (data.result == 'ERR') {
          this.app.alertInfo(this.app.translations.ALERT_Info_InvalidHeaderCalculation);
          return;
        }

        header.appliedCalculationType = type;
        header.appliedCalculationValue = data.result;
        let headersWithCalculations = this.headers.filter(header => header.appliedCalculationType && header.appliedCalculationValue);
        if (headersWithCalculations.length > 0)
          localStorage.setItem(`qx_calulations_${this.nodeId};${this.children[this.selectedTabIndex].name}`, JSON.stringify(headersWithCalculations));
      }, error: (errorLog) => { console.log(errorLog); }
    })
  }

  updateHeaderCalculations(headers: IHeader[]): void {
    if (this.i == headers.length) {
      let headersWithCalculations = this.headers.filter(header => header.appliedCalculationType && header.appliedCalculationValue);
      if (headersWithCalculations.length > 0)
        localStorage.setItem(`qx_calulations_${this.nodeId};${this.children[this.selectedTabIndex].name}`, JSON.stringify(headersWithCalculations));
      return;
    }

    let header = headers[this.i];
    let keys = ['userId', 'nodeId', 'viewName', 'filterString', 'keyValue', 'headerField', 'calcType'];
    let values = [this.userId, this.nodeId, this.children[this.selectedTabIndex].name, this.filterString, this.rowId.toString(), header.field, header.appliedCalculationType];
    let formData = this.app.buildForm(keys, values);
    this.gridService.getHeaderCalculation(formData).subscribe({
      next: (data: any) => {
        if (data.result != 'ERR') {
          header.appliedCalculationValue = data.result;
        }

        if (localStorage.getItem(`qx_calulations_${this.nodeId};${this.children[this.selectedTabIndex].name}`)) {
          let headersWithCalculations = JSON.parse(localStorage.getItem(`qx_calulations_${this.nodeId};${this.children[this.selectedTabIndex].name}`));
          let h = headersWithCalculations.find(h => h.field == header.field);
          if (h) header.appliedCalculationType = h.appliedCalculationType;
        }

        this.i++;
        this.updateHeaderCalculations(headers);
      }, error: (errorLog) => { console.log(errorLog); }
    })
  }

  removeHeaderCalculation(header: IHeader): void {
    header.appliedCalculationType = null;
    header.appliedCalculationValue = null;
    let headersWithCalculations = this.headers.filter(header => header.appliedCalculationType && header.appliedCalculationValue);
    if (headersWithCalculations.length > 0)
      localStorage.setItem(`qx_calulations_${this.nodeId};${this.children[this.selectedTabIndex].name}`, JSON.stringify(headersWithCalculations));
    else
      localStorage.removeItem(`qx_calulations_${this.nodeId};${this.children[this.selectedTabIndex].name}`);
  }

  getHeaderCalculationsFromStorage(): void {
    let headersWithCalculations = [];
    if (localStorage.getItem(`qx_calulations_${this.nodeId};${this.children[this.selectedTabIndex].name}`)) {
      headersWithCalculations = JSON.parse(localStorage.getItem(`qx_calulations_${this.nodeId};${this.children[this.selectedTabIndex].name}`));
      for (let index = 0; index < headersWithCalculations.length; index++) {
        let h = headersWithCalculations[index];
        let header = this.headers.find(header => header.caption == h.caption);
        header.appliedCalculationType = h.appliedCalculationType;
        header.appliedCalculationValue = h.appliedCalculationValue;
      }
    }

    this.i = 0;
    this.updateHeaderCalculations(this.headers.filter(header => header.appliedCalculationType && header.appliedCalculationValue));
  }

  setSelectedLineValueToCopy(text: string): void {
    this.selectedText = text;
  }

  getSafeTooltip(html: string): string {    
    let sanitized = this.sanitizer.sanitize(SecurityContext.HTML, html);
    return sanitized || '';
  }

  @Output() message = new EventEmitter<string>();
  @Output() messageDetail = new EventEmitter<string>();
  backToMobileMainGrid(): void {
    if (this.subDetail) {
      console.log('clicked back from sub detail view');
      this.messageDetail.emit('');
      return;
    }
    this.message.emit('');
  }

  closeSubDetailView(): void {
    this.showMobileDetailView = false;
    this.mobileDetailViewLine = undefined;
  }

  toggleMobileTileSize(): void {   
    if (this.mobileTileSize == 'Small') {
      this.mobileTileSize = 'Medium';
      return;
    }
      
    if (this.mobileTileSize == 'Medium') {
      this.mobileTileSize = 'Large';
      return;
    }

    if (this.mobileTileSize == 'Large') {
      this.mobileTileSize = 'Small';
      return;
    }      
  }

  startLongPress(line: ILine): void {
    this.longPressTimer = setTimeout(() => {
      this.onLongPress(line);
    }, 500);
  }

  cancelLongPress(): void {
    clearTimeout(this.longPressTimer);
  }

  onLongPress(line: ILine) {
    this.contextMenuTop = 50;
    this.contextMenuLeft = 50;
    this.contextMenuType = 'line';
    this.contextMenuVisible = true;
    this.contextMenuObject = line;
  }

  mobileDetailViewLine: ILine|undefined;
  showMobileDetailView: boolean = false;
  openMobileDetailView(line: ILine): void {
    console.log(line);
    this.mobileDetailViewLine = line;
    this.showMobileDetailView  =true;
  }
  //#endregion
}
